import { getAccessToken } from "../config/popupAuth";
import { getSelectedOrganization, getSelectedOrganizationUnitId } from "./organizationService";
import { getSelectedEnvironment } from "./environmentStorage";
import { getApiUrlForEnvironment } from "../config/apiEnvironments";

export const getApiBaseUrl = async () => {
  const environment = await getSelectedEnvironment();
  const baseUrl = getApiUrlForEnvironment(environment);
  return baseUrl + "/apis/novata/excel-addin";
};

export const getScoringApiBaseUrl = async () => {
  const environment = await getSelectedEnvironment();
  const baseUrl = getApiUrlForEnvironment(environment);
  return baseUrl + "/apis/novata";
};

export const TASKPANE_API_ENDPOINTS = {
  ALL_COMPANIES: "/all-companies",
  COMPANIES_BY_ID: "/companies",
  ALL_FUNDS: "/all-funds",
  FUND_BY_ID: "/funds",
  ALL_METRICS: "/all-metrics",
  METRICS_BY_ID: "/metrics",
  ALL_REPORTS: "/all-reports",
  REPORTS_BY_ID: "/reports",
};

export const SCORING_API_ENDPOINTS = {
  RUBRICS: "/rubrics",
  SCORECARDS: "/scorecards",
};

/**
 * Encode query values per Novata OpenAPI rules.
 * Mirrors novata-platform/packages/common/src/utils/qsStringify.ts
 */
const encodeQueryValue = (value) => {
  return encodeURIComponent(String(value))
    .replace(/\(/g, "%28")
    .replace(/\)/g, "%29")
    .replace(/!/g, "%21")
    .replace(/\*/g, "%2A")
    .replace(/\./g, "%2E")
    .replace(/~/g, "%7E");
};

/**
 * Serialize one query param. Keys are not encoded (e.g. rubricIds[0]=uuid).
 * Mirrors qsStringify in novata-platform buildRoute.
 */
const qsStringify = (key, value) => {
  if (typeof value === "number" || typeof value === "string" || typeof value === "boolean") {
    return `${key}=${encodeQueryValue(String(value))}`;
  }
  if (value instanceof Date) {
    return `${key}=${encodeQueryValue(value.toISOString())}`;
  }
  if (Array.isArray(value)) {
    return value.map((item, index) => qsStringify(`${key}[${index}]`, item)).join("&");
  }
  if (value && typeof value === "object") {
    return Object.entries(value)
      .map(([valueKey, item]) => qsStringify(`${key}[${valueKey}]`, item))
      .join("&");
  }
  return "";
};

/** Serialize query params for Novata API routes (scalar + array/object shapes). */
export const buildQueryString = (params = {}) => {
  return Object.entries(params)
    .map(([key, value]) => qsStringify(key, value))
    .filter(Boolean)
    .join("&");
};

export const createScoringApiUrl = async (endpoint, params = {}) => {
  const baseUrl = await getScoringApiBaseUrl();
  let url = baseUrl + endpoint;

  const queryString = buildQueryString(params);
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
};

export const createApiHeaders = (accessToken, orgId, orgUnitId) => {
  const headers = {
    Authorization: "Bearer " + accessToken,
    "X-Novata-App": "NOVATA",
    "novata-app": "EXCEL_ADDIN",
    "novata-organization-id": orgId,
    "Content-Type": "application/json",
  };
  if (orgUnitId) {
    headers["novata-organization-unit-id"] = orgUnitId;
  }
  return headers;
};

export const createApiFetchOptions = (method, headers, body = null) => {
  const options = {
    method,
    headers,
    mode: "cors",
    credentials: "omit",
  };

  if (body) {
    options.body = typeof body === "string" ? body : JSON.stringify(body);
  }

  return options;
};

export const createApiUrl = async (endpoint, params = {}) => {
  const API_BASE_URL = await getApiBaseUrl();
  let url = API_BASE_URL + endpoint;

  const queryString = buildQueryString(params);
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
};

export const getApiAuthCredentials = async () => {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error("No access token available for API calls");
  }

  const selectedOrg = await getSelectedOrganization();
  if (!selectedOrg || !selectedOrg.id) {
    throw new Error("No organization selected");
  }

  const orgUnitId = await getSelectedOrganizationUnitId();

  return {
    accessToken,
    orgId: selectedOrg.id,
    orgUnitId,
  };
};
